import {
  ChangeEvent,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  KeyboardEvent,
} from "react";
import { debounce } from "lodash-es";
import { useShallow } from "zustand/react/shallow";

import Input from "ds/components/Input";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import { Magnify, SparksFilled } from "components/icons/generated";
import useCommandPaletteStore from "components/CommandPalette/store/useCommandPaletteStore";
import ButtonIcon from "ds/components/ButtonIcon";
import useAiChatStore from "components/AssistantPanel/Layout/useStore";
import useAssistantPanelAvailable from "hooks/useAssistantPanelAvailable";

import styles from "./styles.module.css";

const DEBOUNCE_DELAY = 300;

const CommandPaletteHeader = () => {
  const isAssistantPanelAvailable = useAssistantPanelAvailable();
  const {
    currentView,
    subListView,
    searchQuery,
    subListQuery,
    setSearchQuery,
    setSubListQuery,
    clearSubList,
    setModalOpen,
  } = useCommandPaletteStore(
    useShallow((state) => ({
      currentView: state.currentView,
      subListView: state.subListView,
      searchQuery: state.searchQuery,
      subListQuery: state.subListQuery,
      setSearchQuery: state.setSearchQuery,
      setSubListQuery: state.setSubListQuery,
      clearSubList: state.clearSubList,
      setModalOpen: state.setModalOpen,
    }))
  );

  const isSubListView = currentView === "sub-list" && subListView;
  const displayQuery = isSubListView ? subListQuery : searchQuery;

  const [searchValue, setSearchValue] = useState(displayQuery);

  // Use ref to avoid recreating debounced function when setters change
  const currentSetQuery = isSubListView ? setSubListQuery : setSearchQuery;
  const setQueryRef = useRef(currentSetQuery);

  useEffect(() => {
    setQueryRef.current = currentSetQuery;
  }, [currentSetQuery]);

  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => setQueryRef.current(value), DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    setSearchValue(displayQuery);
  }, [displayQuery]);

  useEffect(() => {
    return () => debouncedSetQuery.cancel();
  }, [debouncedSetQuery]);

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSetQuery(value);
    },
    [debouncedSetQuery]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && searchValue === "" && currentView === "sub-list") {
        e.preventDefault();
        clearSubList(true);
      }
    },
    [searchValue, currentView, clearSubList]
  );

  const setAiChatOpen = useAiChatStore(useShallow((state) => state.setIsOpen));
  const handleOpenAiChat = useCallback(() => {
    setModalOpen(false);
    setAiChatOpen(true);
  }, [setModalOpen, setAiChatOpen]);

  return (
    <Box className={styles.header} padding="x-large" gap="medium" align="center" fullWidth>
      <Icon src={Magnify} size="default" noShrink />
      <Input
        placeholder="Type to search"
        value={searchValue}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
      />
      {isAssistantPanelAvailable && (
        <ButtonIcon
          icon={SparksFilled}
          className={styles.aiChatButton}
          size="medium"
          variant="contrast"
          onPress={handleOpenAiChat}
        >
          Ask Saturnhead
        </ButtonIcon>
      )}
    </Box>
  );
};

export default CommandPaletteHeader;
